import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent } from 'devextreme-angular';
import { EventParticipationQuestionCreationModel } from 'src/app/models/event-participations/event-participation-question-creation.model';
import { EventParticipationQuestionTemplateModel } from 'src/app/models/event-participations/event-participation-question-template.model';
import { ParticipationQuestionTypeModel } from 'src/app/models/event-participations/participation-question-type.model';
import { DateSelectionComponent } from '../shared/date-selection/date-selection.component';

type EditableQuestion = EventParticipationQuestionCreationModel & {
  isPending?: boolean;
};

type TranslatedTemplate = EventParticipationQuestionTemplateModel & {
  translatedLabel?: string;
};

@Component({
  selector: 'app-edit-training-participation-questions',
  standalone: false,
  templateUrl: './edit-training-participation-questions.component.html',
  styleUrl: './edit-training-participation-questions.component.scss',
})
export class EditTrainingParticipationQuestionsComponent {
  @Input() templates: TranslatedTemplate[] = [];
  @Input() questions: EditableQuestion[] = [];

  @Input() isViewMode: boolean = false;
  @Input() disableDateSelection: boolean = false;
  
  @ViewChildren(DxFormComponent) dxForms!: QueryList<DxFormComponent>;
  
  @ViewChildren(DateSelectionComponent)
  dateSelections!: QueryList<DateSelectionComponent>;

  availableQuestionTypes = [
    { 
      label: this.translateService.instant('COMMON.FIELDS.PARTICIPATION-QUESTION-TYPES.YES-NO'), 
      value: ParticipationQuestionTypeModel.YesNo 
    },
    { 
      label: this.translateService.instant('COMMON.FIELDS.PARTICIPATION-QUESTION-TYPES.FREE-TEXT'), 
      value: ParticipationQuestionTypeModel.FreeText 
    },
  ];

  availableTemplates: TranslatedTemplate[] = [];
  templateOptions: any[] = [];
  participationTypes = ParticipationQuestionTypeModel;

  today = new Date();
  
  constructor(
    private translateService: TranslateService
  ) {
  }

  ngOnInit() {
    this.questions.forEach(q => {
      if(q.translationKey) {
        q.question = this.translateService.instant(q.translationKey);

        q.options?.forEach(o => {
          if(o.translationKey) {
            o.value = this.translateService.instant(o.translationKey);
          }
        });
      }
    });
    
    this.templates = this.templates.map(t => ({
      ...t,
      translatedLabel: this.translateService.instant(t.translationKey)
    }));

    this.updateAvailableTemplates();
  }

  private updateAvailableTemplates() {
    const usedIds = this.questions
      .filter(q => q.templateId)
      .map(q => q.templateId);
    this.availableTemplates = this.templates.filter(t => !usedIds.includes(t.id));

    this.templateOptions = [
      ...this.availableTemplates.map(t => ({
        type: 'template',
        id: t.id,
        label: t.translatedLabel
      })),
      ...this.availableQuestionTypes.map(qt => ({
        type: 'questionType',
        value: qt.value,
        label: qt.label
      }))
    ];
  }

  addQuestion(
    type: ParticipationQuestionTypeModel | null = null,
    isPending: boolean = true
  ) {
    this.questions.push({
      question: null,
      translationKey: null,
      type: type,
      isRequired: false,
      maxAdditionalPersons: null,
      startHotelDate: null,
      endHotelDate: null,
      options: [],
      templateId: null,
      isPending: isPending
    });
  }

  onTemplateChange(e: any) {
    const selected = e.value;
    if (!selected) return;

    if (selected.type === 'template') {
      this.applyTemplate(selected.id);
      return;
    }

    if (selected.type === 'questionType') {
      this.addQuestion(selected.value, false);
      e.component.reset();
    }
  }

  removeQuestion(index: number) {
    this.questions.splice(index, 1);
    this.updateAvailableTemplates();
  }
 
  applyTemplate(templateId: string) {
    const template = this.templates.find(t => t.id === templateId);
    if (!template) return;

    const newQuestion: EventParticipationQuestionCreationModel = {
      id: null,
      question: this.translateService.instant(template.translationKey),
      translationKey: template.translationKey,
      type: template.type,
      isRequired: false,
      maxAdditionalPersons: null,
      startHotelDate: null,
      endHotelDate: null,
      options: template.options?.map(o => ({ 
        id: null,
        value: this.translateService.instant(o.translationKey),
        translationKey: o.translationKey,
      })) ?? null,
      templateId: templateId
    };
    this.questions.push(newQuestion);
    this.updateAvailableTemplates();
  }

  onRowSelectionChange(e: any, index: number) {
    const selected = e.value;
    if (!selected) return;

    if (selected.type === 'template') {
      this.applyTemplateToRow(selected.id, index);
    }

    if (selected.type === 'questionType') {
      this.questions[index] = {
        ...this.questions[index],
        type: selected.value
      };
    }

    this.questions[index].isPending = false;

    this.updateAvailableTemplates();
    e.component.reset();
  }

  applyTemplateToRow(templateId: string, index: number) {
    const template = this.templates.find(t => t.id === templateId);
    if (!template) return;

    this.questions[index] = {
      id: null,
      question: this.translateService.instant(template.translationKey),
      translationKey: template.translationKey,
      type: template.type,
      isRequired: false,
      maxAdditionalPersons: null,
      startHotelDate: null,
      endHotelDate: null,
      options: template.options?.map(o => ({ 
        id: null,
        value: this.translateService.instant(o.translationKey),
        translationKey: o.translationKey,
      })) ?? null,
      templateId: templateId,
      isPending: false
    };
  }

  public validate(): boolean {
    let firstInvalidHandled = false;
    let allValid = true;

    this.dxForms.forEach(form => {
      const result = form.instance.validate();

      if (!result.isValid) {
        allValid = false;

        if (!firstInvalidHandled) {
          this.scrollToFirstBrokenRule(result);
          firstInvalidHandled = true;
        }
      }
    });

    if(!this.disableDateSelection) {
      this.dateSelections?.forEach(dateSelection => {
        const valid = dateSelection.validateDates();

        if (!valid) {
          allValid = false;

          if (!firstInvalidHandled) {
            dateSelection.elementRef.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            firstInvalidHandled = true;
          }
        }
      });
    }
    
    return allValid;
  }

  private scrollToFirstBrokenRule(result: any): void {
    if (!result?.brokenRules?.length) return;

    const firstBrokenRule = result.brokenRules[0] as any;
    const element = firstBrokenRule?.validator?.$element?.();

    element?.[0]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    element?.[0]?.focus();
  }

  public getQuestions(): EventParticipationQuestionCreationModel[] {
    return this.questions.filter(q => !q.isPending).map(q => {
      if(q.templateId) {
        q.question = null;
        q.options?.forEach(o => {
          o.value = null;
        });
      }
      return q;
    });
  }
}